/**
 * Image conversion & compression utilities.
 * – WebP / HEIC → JPEG conversion
 * – Resize to stay under a byte-size limit
 */

/**
 * Convert a File to JPEG if it's WebP or HEIC/HEIF.
 * Returns the original file untouched for JPG/PNG/PDF.
 */
export async function convertToJpegIfNeeded(file: File): Promise<File> {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();

  // HEIC / HEIF
  if (type === "image/heic" || type === "image/heif" || name.endsWith(".heic") || name.endsWith(".heif")) {
    const heic2any = (await import("heic2any")).default;
    const blob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 }) as Blob;
    const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
    return new File([blob], newName, { type: "image/jpeg" });
  }

  // WebP
  if (type === "image/webp") {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(bitmap, 0, 0);
    const blob = await canvasToBlob(canvas, "image/jpeg", 0.92);
    const newName = file.name.replace(/\.webp$/i, ".jpg");
    return new File([blob], newName, { type: "image/jpeg" });
  }

  return file;
}

/**
 * Resize an image File so it's under `maxBytes`.
 * Reduces quality first, then dimensions.
 * Returns a JPEG File.
 */
export async function resizeImageUnder(file: File, maxBytes: number): Promise<File> {
  if (file.size <= maxBytes) return file;

  const bitmap = await createImageBitmap(file);
  let { width, height } = bitmap;
  let quality = 0.85;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, "image/jpeg", quality);

    if (blob.size <= maxBytes || (quality <= 0.3 && width <= 400)) {
      const newName = file.name.replace(/\.[^.]+$/, "_sm.jpg");
      return new File([blob], newName, { type: "image/jpeg" });
    }

    if (quality > 0.3) {
      quality -= 0.1;
    } else {
      // scale down by 20 %
      width = Math.round(width * 0.8);
      height = Math.round(height * 0.8);
    }
  }
}

export function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Canvas toBlob failed"))),
      type,
      quality,
    );
  });
}
